/**
 * Thumbnail Cache Utility
 * Manages caching of video thumbnails to improve performance
 */

import logger from './logger';

interface CachedThumbnail {
  url: string;
  timestamp: number;
  blob?: Blob;
}

class ThumbnailCache {
  private cache = new Map<string, CachedThumbnail>();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly MAX_CACHE_SIZE = 100; // Maximum number of cached thumbnails

  /**
   * Get a cached thumbnail URL
   */
  get(videoId: string): string | null {
    const cached = this.cache.get(videoId);
    
    if (!cached) {
      return null;
    }

    // Check if cache entry is expired
    if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(videoId);
      logger.debug(`Thumbnail cache expired for video: ${videoId}`);
      return null;
    }
    return cached.url;
  }

  /**
   * Set a thumbnail in the cache
   */
  set(videoId: string, url: string, blob?: Blob): void {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.evictOldest();
    }

    this.cache.set(videoId, {
      url,
      timestamp: Date.now(),
      blob
    });

    logger.debug(`Thumbnail cached for video: ${videoId}`);
  }

  /**
   * Remove a specific thumbnail from cache
   */
  remove(videoId: string): void {
    const removed = this.cache.delete(videoId);
    if (removed) {
      logger.debug(`Thumbnail removed from cache for video: ${videoId}`);
    }
  }

  /**
   * Clear all cached thumbnails
   */
  clear(): void {
    this.cache.clear();
    logger.debug('Thumbnail cache cleared');
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; maxSize: number; cacheDuration: number } {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      cacheDuration: this.CACHE_DURATION
    };
  }

  /**
   * Evict the oldest cache entry
   */
  private evictOldest(): void {
    let oldestKey = '';
    let oldestTimestamp = Date.now();

    for (const [key, value] of Array.from(this.cache.entries())) {
      if (value.timestamp < oldestTimestamp) {
        oldestTimestamp = value.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      logger.debug(`Evicted oldest thumbnail from cache: ${oldestKey}`);
    }
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    const toDelete: string[] = [];

    for (const [key, value] of Array.from(this.cache.entries())) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        toDelete.push(key);
      }
    }

    toDelete.forEach(key => this.cache.delete(key));
    
    if (toDelete.length > 0) {
      logger.debug(`Cleaned up ${toDelete.length} expired thumbnail(s) from cache`);
    }
  }

  /**
   * Preload a thumbnail
   */
  async preload(videoId: string, thumbnailUrl: string): Promise<void> {
    try {
      const response = await fetch(thumbnailUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      this.set(videoId, objectUrl, blob);
      logger.debug(`Preloaded thumbnail for video: ${videoId}`);
    } catch (error) {
      logger.error(`Failed to preload thumbnail for video ${videoId}:`, error);
    }
  }

  /**
   * Preload a thumbnail with time-based URL generation
   * Used by VideoDetails component for clip thumbnails
   */
  async preloadThumbnail(
    clipId: string, 
    sourceVideoUrl: string, 
    thumbnailTime: number
  ): Promise<string> {
    try {
      // Check if already cached
      const cached = this.get(clipId);
      if (cached) {
        return cached;
      }

      // Generate thumbnail URL (this would typically call your backend API)
      const thumbnailUrl = `${sourceVideoUrl}?t=${thumbnailTime}`;
      
      // For now, just cache the URL directly
      // In a real implementation, you might generate actual thumbnail images
      this.set(clipId, thumbnailUrl);
      
      logger.debug(`Preloaded thumbnail for clip: ${clipId} at time ${thumbnailTime}s`);
      return thumbnailUrl;
    } catch (error) {
      logger.error(`Failed to preload thumbnail for clip ${clipId}:`, error);
      // Return a fallback URL or empty string
      return '';
    }
  }
}

// Export singleton instance
const thumbnailCache = new ThumbnailCache();

// Set up periodic cleanup
if (typeof window !== 'undefined') {
  setInterval(() => {
    thumbnailCache.cleanup();
  }, 5 * 60 * 1000); // Clean up every 5 minutes
}

export default thumbnailCache;
